import React from 'react';
import { Shield, Lock, Eye, Database, Bell, Mail, Network } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600">
              Last updated: February 6, 2025
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700 leading-relaxed">
                We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and safeguard your information when you use our 
                Android application.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-gray-700 leading-relaxed ml-13">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Usage Data</h3>
                  <p>The app does not require user registration and does not directly collect personally identifiable information such as name, email address, or phone number.</p>
                  {/* <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                    <li>App features accessed</li>
                    <li>Interaction patterns and preferences</li>
                    <li>Device information (model, operating system version)</li>
                    <li>Crash reports and performance data</li>
                  </ul> */}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Accessibility Service Usage</h3>
                  <p>Touchpad for Big Phone & Tab uses Android’s Accessibility Service solely to provide touchpad-based cursor control and screen interaction.</p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                    <li>No keystrokes are logged</li>
                    <li>No text or password input is captured</li>
                    <li>No screen content is recorded or transmitted</li>
                    <li>No accessibility data leaves the device</li>
                  </ul>
                  <p className="mt-2">All accessibility-related processing occurs locally on your device.</p>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            {/* <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p className="mb-3">We use the collected information to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Provide and maintain the touchpad functionality</li>
                  <li>Improve and optimize app performance</li>
                  <li>Analyze usage patterns to enhance user experience</li>
                  <li>Fix bugs and technical issues</li>
                  <li>Display relevant advertisements (see Advertising section below)</li>
                </ul>
              </div>
            </section> */}

            {/* Advertising */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Advertising</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed space-y-3">
                <p>
                  Our app displays advertisements to support free access to our services. We work with 
                  third-party advertising partners who may collect and use information about your device 
                  and app usage to show you relevant ads.
                </p>
                <p>
                  These advertising partners may use cookies, device identifiers, and similar technologies 
                  to collect information. You can opt out of personalized advertising through your device 
                  settings (Google Settings → Ads → Opt out of Ads Personalization).
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Network className="w-5 h-5 text-pink-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Third-Party Services</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed space-y-3">
                <p>Our app may use third-party services that collect information used to identify you, including:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Google Play Services</li>
                  <li>Google Analytics for Firebase</li>
                  <li>AdMob (Google Mobile Advertising)</li>
                </ul>
                <p className="mt-3">
                  These services have their own privacy policies. We encourage you to review them:
                </p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline">Google Privacy Policy</a></li>
                  <li><a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline">AdMob Privacy Policy</a></li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Data Sharing</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p>
                  We do not sell user data. Data is shared only with Google services (AdMob and Firebase) as required for advertising and analytics.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p>
                  We take reasonable measures to protect user data. All data processing is handled by trusted Google services in accordance with their security standards.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Children's Privacy</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p>
                  Our app is not intended for children under 13 years of age. We do not knowingly collect 
                  personal information from children under 13.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            {/* <section>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Your Rights</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p className="mb-3">You have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Opt out of personalized advertising</li>
                </ul>
              </div>
            </section> */}

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update our privacy policy from time to time. We will notify you of any changes by 
                posting the new privacy policy on this page and updating the "Last updated" date. You are 
                advised to review this privacy policy periodically for any changes.
              </p>
            </section>

            {/* Contact Us */}
            <section className="border-t pt-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
              </div>
              <div className="ml-13 text-gray-700 leading-relaxed">
                <p className="mb-3">
                  If you have any questions or concerns about this privacy policy or our data practices, 
                  please contact us:
                </p>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="font-semibold text-gray-900">Email:</p>
                  <a href="mailto:mpointerquery@gmail.com" className="text-indigo-600 hover:text-indigo-700">
                    mpointerquery@gmail.com
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
